import React, { useState, useEffect, useRef, useCallback } from "react";
import { useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { GetPayrollDetails, getPayrollItemAccounts, getAccounts } from "../../api";
import { cleanFrequency } from "../../HelperFunctions/PayrollEntryHelpers";
import { useAlerts } from "../../Hooks";
import { filterAccounts, parseHours, formatHours, getItemType } from "./wageTableUtils";
import { ITEM_TYPE } from "../../Enums/Enum";

/**
 * Custom hook that encapsulates all state and data-fetching logic
 * for EmployeeWageMappingTable. Extracts ~600 lines from the component.
 */
export function useWageMappingTable({
    row,
    formValues,
    corporationId,
    accountOptions,
    wageItems,
    onTotalGrossChange,
    setLoadingDetails,
    setInitialState,
    refreshTrigger,
    manualDeptJobChange,
    isReadOnly,
    skipPayrollDetailsLoad,
    isReportScreen: propIsReportScreen,
    isCompletedRecord: propIsCompletedRecord,
    isNewEmployeeJustAdded,
}) {
    const { values, setFieldValue, errors, touched } = useFormikContext();
    const valuesRef = useRef(values);
    useEffect(() => { valuesRef.current = values; }, [values]);

    const { enqueueSnackbar } = useSnackbar();
    const [removingIndex, setRemovingIndex] = useState(null);
    const { popup: alertPopup, showDeleteConfirm, hidePopup } = useAlerts();
    const [lastManualChange, setLastManualChange] = useState(0);
    const [openAddCOA, setOpenAddCOA] = useState(false);
    const [pendingCOAField, setPendingCOAField] = useState("");

    const payrollRequestRef = useRef(0);
    const lastFetchedPayloadRef = useRef("");
    const lastManualChangeRef = useRef(0);
    const prevDeptRef = useRef(formValues?.department);
    const prevJobRef = useRef(formValues?.job);

    // ─── Derived flags ────────────────────────────────────────────────────
    const importedEmpName = formValues?.importedName ?? values?.importedName ?? row?.raw?.ImportedEmployeeName ?? row?.importedName;
    const isImportedNameEmpty = !importedEmpName || String(importedEmpName).trim() === "" || String(importedEmpName).toLowerCase() === "null" || String(importedEmpName).toLowerCase() === "undefined";
    const isType1 = isImportedNameEmpty || values.importPayrollType === 1 || values?.isAddPayRoll;
    const showItemColumn = !isType1 && values.importPayrollType === 2;

    const isReport = Boolean(propIsReportScreen || row?.isReport || row?.fromReport);
    const isCompleted = Boolean(
        propIsCompletedRecord || skipPayrollDetailsLoad || row?.skipPayrollDetailsLoad ||
        row?.statusText === "Matched" || row?.statusText === "Completed" ||
        row?.view === "Matched" || row?.view === "Completed" ||
        String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "matched" ||
        String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "completed" ||
        row?.isCompleted || row?.raw?.IsCompleted
    );
    const isUnmapped = !isReport && !isCompleted && Boolean(
        row?.view === "Unmapped" || row?.statusText === "Unmapped" ||
        String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "unmapped" ||
        isNewEmployeeJustAdded || row?.isNewEmployeeJustAdded || row?.isUnmapped || !row?.raw?.MappingStatus
    );

    // ─── Account options ──────────────────────────────────────────────────
    const [localAccountOptions, setLocalAccountOptions] = useState([]);

    useEffect(() => {
        setLocalAccountOptions(filterAccounts(accountOptions || []));
    }, [accountOptions]);

    const enhancedAccountOptions = React.useMemo(() => {
        const filtered = (localAccountOptions || []).filter(o => o.value !== "");
        return [{ label: "--Select--", value: "" }, { label: "Add New", value: "ADD_NEW" }, ...filtered];
    }, [localAccountOptions]);

    const accountOptionsMap = React.useMemo(() => {
        const map = new Map();
        enhancedAccountOptions.forEach(opt => {
            const strVal = String(opt.value || "");
            map.set(strVal, opt);
            map.set(strVal.toLowerCase(), opt);
            map.set(strVal.toUpperCase(), opt);
        });
        return map;
    }, [enhancedAccountOptions]);

    const validCOASet = React.useMemo(() => new Set((localAccountOptions || []).map(acc => String(acc.value))), [localAccountOptions]);

    // ─── Wage options ─────────────────────────────────────────────────────
    const wageOptions = React.useMemo(() => [
        { label: "--Select--", value: "" },
        { label: "-Add New-", value: "ADD_NEW" },
        ...(wageItems || []).map(item => ({ label: item.PayrollItemName || item.ItemName, value: item.ItemID }))
    ], [wageItems]);


    const normalizedOptionsMap = React.useMemo(() => {
        const map = new Map();
        wageOptions.forEach(opt => map.set(String(opt.value).toLowerCase(), opt));
        return map;
    }, [wageOptions]); // eslint-disable-line

    // ─── Refresh accounts after QuickAdd COA ─────────────────────────────
    const refreshAccounts = useCallback(async () => {
        try {
            const freshAccounts = await getAccounts(corporationId || values?.corporationId || values?.corpId || "", "000000000000000000000000000000000000", true);
            const formattedAccounts = freshAccounts?.Accounts || freshAccounts || [];
            setLocalAccountOptions([{ label: "--Select--", value: "", type: "" }, ...filterAccounts(formattedAccounts)]);
        } catch (error) {
            console.error(error);
        }
    }, [corporationId, values?.corporationId, values?.corpId]);

    // ─── Refresh trigger (QuickAdd success) ───────────────────────────────
    useEffect(() => {
        if (!refreshTrigger?.trigger) return;
        const { id, rowIndex, arrayName } = refreshTrigger;
        if (id && rowIndex !== undefined && arrayName === "wage") {
            const updatedWages = [...values.wage];
            updatedWages[rowIndex] = { ...updatedWages[rowIndex], mappedTo: id, coa: "" };
            setFieldValue("wage", updatedWages);
            handleCOA({ value: id }, rowIndex); // eslint-disable-line
        }
    }, [refreshTrigger]); // eslint-disable-line

    // ─── Total gross sync ─────────────────────────────────────────────────
    useEffect(() => {
        const totalGross = values.wage.reduce((sum, r) => sum + Number(r.gross || 0), 0);
        onTotalGrossChange?.(totalGross);
    }, [values]); // eslint-disable-line

    // ─── Auto-load payroll details on dept/job change ─────────────────────
    useEffect(() => {
        if (!isType1) return;

        const forceOverwrite = manualDeptJobChange > lastManualChangeRef.current;
        const deptId = formValues.department || row?.raw?.DepartmentID || row?.raw?.IncDeptID;
        const empId = formValues.employeeId || row?.raw?.EmployeeID || row?.raw?.EmployeeId;

        if (forceOverwrite) {
            if (deptId && empId) {
                lastManualChangeRef.current = manualDeptJobChange;
                setLastManualChange(manualDeptJobChange);
                const isDeptChanged = formValues?.department !== prevDeptRef.current;
                prevDeptRef.current = formValues?.department;
                prevJobRef.current = formValues?.job;
                getPayrollDetailsInternal(true, isDeptChanged);
            }
            return;
        }

        if (isReport || isCompleted || !isUnmapped) return;

        const isWageEmpty = (valuesRef.current?.wage || []).length === 0 ||
            (valuesRef.current?.wage.length === 1 && !valuesRef.current?.wage[0].wageName && !valuesRef.current?.wage[0].mappedTo && (!valuesRef.current?.wage[0].gross || Number(valuesRef.current?.wage[0].gross) === 0));

        if (deptId && empId && isWageEmpty) {
            getPayrollDetailsInternal(false, true);
        }
    }, [formValues.department, formValues.job, formValues.employeeId, formValues.profitCenter, manualDeptJobChange, isNewEmployeeJustAdded, isType1, isReport, isCompleted, isUnmapped]); // eslint-disable-line

    // ─── COA fetch on mapped-to selection ────────────────────────────────
    const handleCOA = useCallback(async (item, index) => {
        if (!formValues?.department || !formValues?.job) return;
        const coaPayload = {
            CorpID: corporationId,
            PayrollItemID: item.value,
            IncDeptID: formValues?.department,
            JobID: formValues?.job
        };
        const [payrollItemAcc, freshAccounts] = await Promise.all([
            getPayrollItemAccounts(coaPayload),
            getAccounts(corporationId)
        ]);
        const preCoa = payrollItemAcc?.PayrollItemAccounts[0] || {};
        const fetchedCoaId = preCoa?.AccountID || "";
        let finalCoa = "";
        if (fetchedCoaId) {
            const accountsArray = freshAccounts?.Accounts || freshAccounts || [];
            const isValid = accountsArray.some(acc => String(acc.ID || acc.value) === String(fetchedCoaId));
            if (isValid) finalCoa = fetchedCoaId;
        }
        setFieldValue(`wage[${index}].coa`, finalCoa);
        setFieldValue(`wage[${index}].coaName`, finalCoa ? (preCoa?.AccountName || "") : "");
    }, [corporationId, formValues?.department, formValues?.job, setFieldValue]);

    // ─── Internal: getPayrollDetails ──────────────────────────────────────
    async function getPayrollDetailsInternal(forceOverwrite = false, isDeptChanged = true) {
        if (!isType1) return;
        if (!forceOverwrite && (isReport || isCompleted || !isUnmapped)) return;

        const deptId = formValues?.department || row?.raw?.DepartmentID || row?.raw?.IncDeptID;
        const empId = formValues?.employeeId || row?.raw?.EmployeeID || row?.raw?.EmployeeId;
        if (!empId || !deptId) return;

        const isWageEmpty = (values.wage || []).length === 0 ||
            (values.wage.length === 1 && !values.wage[0].wageName && !values.wage[0].mappedTo && (!values.wage[0].gross || Number(values.wage[0].gross) === 0));
        if (!forceOverwrite && !isWageEmpty) return;

        const isAddDedEmpty = (values.addAndDeductions || []).length === 0 ||
            (values.addAndDeductions.length === 1 && !values.addAndDeductions[0].addDeduction && !values.addAndDeductions[0].mappedTo && (!values.addAndDeductions[0].amount || Number(values.addAndDeductions[0].amount) === 0));
        const isTaxesEmpty = (values.eeAndErTaxes || []).length === 0 ||
            (values.eeAndErTaxes.length === 1 && !values.eeAndErTaxes[0].taxType && !values.eeAndErTaxes[0].mappedTo && (!values.eeAndErTaxes[0].amount || Number(values.eeAndErTaxes[0].amount) === 0));
        const isEffectivelyEmpty = isWageEmpty && isAddDedEmpty && isTaxesEmpty;

        const isUnmappedState =
            (values.wage || []).some(w => !w.mappedTo || !w.coa) ||
            (values.addAndDeductions || []).some(a => !a.mappedTo || !a.coa) ||
            (values.eeAndErTaxes || []).some(t => !t.mappedTo || !t.coa);

        if (!isEffectivelyEmpty && !isUnmappedState && !forceOverwrite) return;

        const prDetailsPayload = {
            CorpID: values.corporationId || corporationId || values.corporation?.corpValue || values.corpId || formValues?.corporationId || row?.raw?.CorporationID,
            IncDeptID: deptId,
            EmployeeId: empId,
            ImportPayrollType: isType1 ? 1 : values.importPayrollType,
        };
        const jobId = formValues?.job || row?.raw?.JobID || row?.raw?.JobId;
        if (jobId) prDetailsPayload.JobId = jobId;
        if (isType1) prDetailsPayload.Frequency = cleanFrequency(values.frequency || values.payFrequency || formValues?.payFrequency || formValues?.frequencyID);
        const pcId = formValues?.profitCenter || row?.raw?.PCID || row?.raw?.ProfitCenterID;
        if (pcId) prDetailsPayload.PCID = pcId;

        const payloadKey = JSON.stringify(prDetailsPayload);
        if (!forceOverwrite && lastFetchedPayloadRef.current === payloadKey) return;

        const requestId = ++payrollRequestRef.current;
        lastFetchedPayloadRef.current = payloadKey;

        const emptyRow = { wageName: "", mappedTo: "", mappedToName: "", coa: "", hrs: values.IsHoursDecimal ? "0.00" : "0:00", rate: "0.00", gross: "0.00" };
        const emptyAddDedRow = { id: Date.now(), addDeduction: "", item: "", mappedTo: "", mappedToName: "", coa: "", coaName: "", amount: "0.00" };
        const emptyTaxRow = { id: Date.now() + 1, taxType: "", item: "", mappedTo: "", mappedToName: "", coa: "", coaName: "", expenseCoa: "", amount: "0.00" };

        if (forceOverwrite) {
            setFieldValue("wage", [emptyRow]);
            if (isDeptChanged) {
                setFieldValue("addAndDeductions", [emptyAddDedRow]);
                setFieldValue("eeAndErTaxes", [emptyTaxRow]);
            }
        }

        setLoadingDetails(true);
        try {
            const details = await GetPayrollDetails(prDetailsPayload);
            if (requestId !== payrollRequestRef.current) return;

            const rawList = Array.isArray(details)
                ? details
                : (details?.EmpPayrollItemDetails || details?.empPayrollItemDetails || details?.PayrollDetails || details?.data || details?.Items || []);
            const data = Array.isArray(rawList) ? rawList : [];

            const statusCode = details?.StatusCode ?? details?.statusCode;
            const isExplicitFailure = (statusCode !== undefined && statusCode !== null && statusCode !== 200 && statusCode !== "200") ||
                (details?.Status && typeof details.Status === "string" && details.Status.toLowerCase().includes("fail") && data.length === 0);

            if (isExplicitFailure) {
                if (forceOverwrite) {
                    setFieldValue("wage", [emptyRow]);
                    if (isDeptChanged) {
                        setFieldValue("addAndDeductions", [emptyAddDedRow]);
                        setFieldValue("eeAndErTaxes", [emptyTaxRow]);
                    }
                } else if (isEffectivelyEmpty) {
                    setFieldValue("wage", [emptyRow]);
                    setFieldValue("addAndDeductions", [emptyAddDedRow]);
                    setFieldValue("eeAndErTaxes", [emptyTaxRow]);
                }
                return;
            }

            // Map wage rows
            let mappedWages = mapWageRows(data, forceOverwrite, isEffectivelyEmpty, isDeptChanged, values, emptyRow, emptyAddDedRow, emptyTaxRow, accountOptionsMap, isType1);
            let mappedAddDed = [];
            let mappedTaxes = [];

            if (mappedWages.length > 0 || forceOverwrite || isEffectivelyEmpty) {
                setFieldValue("wage", mappedWages.length > 0 ? mappedWages : [emptyRow]);
            }

            if (isDeptChanged) {
                mappedAddDed = mapAddDedRows(data, forceOverwrite, isEffectivelyEmpty, values, emptyAddDedRow, accountOptionsMap, isType1);
                if (mappedAddDed.length > 0 || forceOverwrite || isEffectivelyEmpty) {
                    setFieldValue("addAndDeductions", mappedAddDed.length > 0 ? mappedAddDed : [emptyAddDedRow]);
                }
                mappedTaxes = mapTaxRows(data, forceOverwrite, isEffectivelyEmpty, values, emptyTaxRow, accountOptionsMap, isType1);
                if (mappedTaxes.length > 0 || forceOverwrite || isEffectivelyEmpty) {
                    setFieldValue("eeAndErTaxes", mappedTaxes.length > 0 ? mappedTaxes : [emptyTaxRow]);
                }
            }

            if (!forceOverwrite && setInitialState) {
                const finalWages = mappedWages.length > 0 ? mappedWages : [emptyRow];
                const finalAddDed = mappedAddDed.length > 0 ? mappedAddDed : (values?.addAndDeductions?.length > 0 ? values.addAndDeductions : [emptyAddDedRow]);
                const finalTaxes = mappedTaxes.length > 0 ? mappedTaxes : (values?.eeAndErTaxes?.length > 0 ? values.eeAndErTaxes : [emptyTaxRow]);
                setInitialState({
                    formValues: {
                        name: formValues?.name || "", department: formValues?.department || "", job: formValues?.job || "",
                        checkNo: formValues?.checkNo || "", profitCenter: formValues?.profitCenter || "",
                        employeeId: formValues?.employeeId || "", account: values?.account || "",
                    },
                    values: { wage: finalWages, addAndDeductions: finalAddDed, eeAndErTaxes: finalTaxes, account: values?.account || "" }
                });
            }
        } catch (error) {
            lastFetchedPayloadRef.current = "";
            enqueueSnackbar("Error while getting Payroll Details", { variant: "error" });
        } finally {
            setLoadingDetails(false);
        }
    }

    const totalGross = values.wage.reduce((sum, r) => sum + Number(r.gross || 0), 0);

    return {
        values, setFieldValue, errors, touched,
        enqueueSnackbar,
        removingIndex, setRemovingIndex,
        alertPopup, showDeleteConfirm, hidePopup,
        openAddCOA, setOpenAddCOA,
        pendingCOAField, setPendingCOAField,
        isType1, showItemColumn,
        wageOptions, normalizedOptionsMap,
        enhancedAccountOptions, accountOptionsMap, validCOASet,
        totalGross,
        handleCOA,
        refreshAccounts,
        lastManualChange,
    };
}

// ─── Pure mapping helpers (no React) ──────────────────────────────────────────

function mapWageRows(data, forceOverwrite, isEffectivelyEmpty, isDeptChanged, values, emptyRow, emptyAddDedRow, emptyTaxRow, accountOptionsMap, isType1) {
    const wageData = data.filter(item => {
        const type = getItemType(item);
        return type === ITEM_TYPE.Wages || type === null || type === 0 || isNaN(type);
    });

    if (!isType1 && values.importPayrollType === 2) {
        if ((values.wage || []).length === 0) return [];
        return (values.wage || []).map(row => {
            const rowName = (row.wageName || "").trim().toLowerCase();
            let match = wageData.find(item => {
                const impName = (item.ImportedItemName || "").trim().toLowerCase();
                const itName = (item.PayrollItemName || item.ItemName || item.itemName || "").trim().toLowerCase();
                return (impName && impName === rowName) || (itName && itName === rowName);
            });
            if (!match && wageData.length === 1 && (values.wage || []).length === 1) match = wageData[0];
            if (match) {
                const mappedToVal = match.PayrollItemID || match.ItemID || match.PayrollItemId || match.itemId || match.Id || "";
                const mappedToLabel = match.PayrollItemName || match.ItemName || match.itemName || "";
                const coaVal = match.AccountID || match.COA || match.AccountId || match.coa || "";
                const account = accountOptionsMap.get(String(coaVal));
                return { ...row, mappedTo: mappedToVal, PayrollItemId: mappedToVal, mappedToName: mappedToLabel, coa: coaVal, coaName: match.AccountName || match.COAName || account?.label || "" };
            } else if (forceOverwrite) {
                return { ...row, mappedTo: "", mappedToName: "", coa: "" };
            }
            return row;
        });
    }

    const mapped = wageData.map((item, index) => {
        const existingRow = (!forceOverwrite && values.wage && values.wage[index]) || {};
        const mappedToVal = item.PayrollItemID || item.ItemID || item.PayrollItemId || item.itemId || item.Id || "";
        const mappedToLabel = item.PayrollItemName || item.ItemName || item.itemName || "";
        const coaVal = item.AccountID || item.COA || item.AccountId || item.coa || "";
        const account = accountOptionsMap.get(String(coaVal));
        const coaLabel = item.AccountName || item.COAName || item.accountName || account?.label || "";
        const itemRate = item.Rate ?? item.rate;
        const newRate = (itemRate !== null && itemRate !== undefined && String(itemRate).trim() !== "") ? Number(itemRate).toFixed(2) : (forceOverwrite ? "0.00" : (existingRow.rate || "0.00"));
        const itemAmt = item.Amount ?? item.amount;
        let newGross = "0.00";
        if (isType1 && itemAmt !== null && itemAmt !== undefined && String(itemAmt).trim() !== "") newGross = Number(itemAmt).toFixed(2);
        else if (!forceOverwrite && existingRow.gross) newGross = existingRow.gross;
        let formattedHrs = values.IsHoursDecimal ? "0.00" : "0:00";
        if (!forceOverwrite && existingRow.hrs !== undefined && existingRow.hrs !== "") {
            const parsed = parseHours(existingRow.hrs, values.IsHoursDecimal);
            formattedHrs = values.IsHoursDecimal ? parsed.toFixed(2) : formatHours(parsed);
            if (Number(newRate) > 0 && parsed > 0 && (!itemAmt || Number(itemAmt) === 0)) newGross = (parsed * Number(newRate)).toFixed(2);
        }
        return { ...existingRow, id: (!forceOverwrite && existingRow.id) ? existingRow.id : Date.now() + index, wageName: (!forceOverwrite && existingRow.wageName) ? existingRow.wageName : mappedToLabel, mappedTo: mappedToVal, PayrollItemId: mappedToVal, mappedToName: mappedToLabel, coa: coaVal, coaName: coaLabel, hrs: formattedHrs, rate: newRate, gross: newGross };
    });

    if (!forceOverwrite && values.wage && values.wage.length > wageData.length) {
        for (let i = wageData.length; i < values.wage.length; i++) {
            const row = values.wage[i];
            const hrsVal = parseHours(row.hrs, values.IsHoursDecimal);
            if (row.wageName || hrsVal !== 0 || (row.gross && Number(row.gross) !== 0)) {
                let fHrs = values.IsHoursDecimal ? "0.00" : "0:00";
                if (row.hrs !== undefined && row.hrs !== "") fHrs = values.IsHoursDecimal ? hrsVal.toFixed(2) : formatHours(hrsVal);
                mapped.push({ ...row, mappedTo: "", mappedToName: "", coa: "", hrs: fHrs });
            }
        }
    }
    return mapped;
}

function mapAddDedRows(data, forceOverwrite, isEffectivelyEmpty, values, emptyAddDedRow, accountOptionsMap, isType1) {
    const addDedData = data.filter(item => { const type = getItemType(item); return type === ITEM_TYPE.Additions || type === ITEM_TYPE.Deductions; });
    if (!isType1 && values.importPayrollType === 2) {
        if ((values.addAndDeductions || []).length === 0) return [];
        return (values.addAndDeductions || []).map(row => {
            const rowItemName = (row.item || "").trim().toLowerCase();
            const rowType = Number(row.addDeduction);
            let match = addDedData.find(item => {
                const impName = (item.ImportedItemName || "").trim().toLowerCase();
                const itName = (item.PayrollItemName || item.ItemName || item.itemName || "").trim().toLowerCase();
                const itType = getItemType(item);
                const typeMatch = !rowType || rowType === itType;
                return typeMatch && ((impName && impName === rowItemName) || (itName && itName === rowItemName));
            });
            if (match) {
                const v = match.PayrollItemID || match.ItemID || match.PayrollItemId || match.itemId || match.Id || "";
                const l = match.PayrollItemName || match.ItemName || match.itemName || "";
                const c = match.AccountID || match.COA || match.AccountId || match.coa || "";
                const acc = accountOptionsMap.get(String(c));
                return { ...row, addDeduction: row.addDeduction || getItemType(match), mappedTo: v, PayrollItemId: v, mappedToName: l, coa: c, coaName: match.AccountName || match.COAName || acc?.label || "" };
            } else if (forceOverwrite) return { ...row, mappedTo: "", mappedToName: "", coa: "", coaName: "" };
            return row;
        });
    }
    const mapped = addDedData.map((item, index) => {
        const existingRow = (!forceOverwrite && values.addAndDeductions && values.addAndDeductions[index]) || {};
        const v = item.PayrollItemID || item.ItemID || item.PayrollItemId || item.itemId || item.Id || "";
        const l = item.PayrollItemName || item.ItemName || item.itemName || "";
        const c = item.AccountID || item.COA || item.AccountId || item.coa || "";
        const acc = accountOptionsMap.get(String(c));
        const itemAmt = item.Amount ?? item.amount;
        return { ...existingRow, id: (!forceOverwrite && existingRow.id) ? existingRow.id : Date.now() + index, addDeduction: getItemType(item) || existingRow.addDeduction, item: (!forceOverwrite && existingRow.item) ? existingRow.item : l, mappedTo: v, PayrollItemId: v, mappedToName: l, coa: c, coaName: item.AccountName || item.COAName || acc?.label || "", amount: (!forceOverwrite && existingRow.amount) ? existingRow.amount : (itemAmt != null ? Number(itemAmt).toFixed(2) : "0.00") };
    });
    if (!forceOverwrite && values.addAndDeductions && values.addAndDeductions.length > addDedData.length) {
        for (let i = addDedData.length; i < values.addAndDeductions.length; i++) {
            const row = values.addAndDeductions[i];
            if (row.item || (row.amount && Number(row.amount) !== 0)) mapped.push({ ...row, mappedTo: "", mappedToName: "", coa: "", coaName: "" });
        }
    }
    return mapped;
}

function mapTaxRows(data, forceOverwrite, isEffectivelyEmpty, values, emptyTaxRow, accountOptionsMap, isType1) {
    const taxesData = data.filter(item => { const type = getItemType(item); return type === ITEM_TYPE.EmployeeTaxes || type === ITEM_TYPE.EmployerTaxes; });
    if (!isType1 && values.importPayrollType === 2) {
        if ((values.eeAndErTaxes || []).length === 0) return [];
        return (values.eeAndErTaxes || []).map(row => {
            const rowItemName = (row.item || "").trim().toLowerCase();
            const rowType = Number(row.taxType);
            let match = taxesData.find(item => {
                const impName = (item.ImportedItemName || "").trim().toLowerCase();
                const itName = (item.PayrollItemName || item.ItemName || item.itemName || "").trim().toLowerCase();
                const itType = getItemType(item);
                const typeMatch = !rowType || rowType === itType;
                return typeMatch && ((impName && impName === rowItemName) || (itName && itName === rowItemName));
            });
            if (match) {
                const v = match.PayrollItemID || match.ItemID || match.PayrollItemId || match.itemId || match.Id || "";
                const l = match.PayrollItemName || match.ItemName || match.itemName || "";
                const c = match.AccountID || match.COA || match.AccountId || match.coa || "";
                const acc = accountOptionsMap.get(String(c));
                const isErTax = getItemType(match) === ITEM_TYPE.EmployerTaxes;
                const expCoa = isErTax ? (match.LiabilityAccountID || match.ExpenseAccountID || match.ExpenseCOA || "") : "";
                return { ...row, taxType: row.taxType || getItemType(match), mappedTo: v, PayrollItemId: v, mappedToName: l, coa: c, coaName: match.AccountName || match.COAName || acc?.label || "", expenseCoa: expCoa };
            } else if (forceOverwrite) return { ...row, mappedTo: "", mappedToName: "", coa: "", coaName: "", expenseCoa: "" };
            return row;
        });
    }
    const mapped = taxesData.map((item, index) => {
        const existingRow = (!forceOverwrite && values.eeAndErTaxes && values.eeAndErTaxes[index]) || {};
        const v = item.PayrollItemID || item.ItemID || item.PayrollItemId || item.itemId || item.Id || "";
        const l = item.PayrollItemName || item.ItemName || item.itemName || "";
        const c = item.AccountID || item.COA || item.AccountId || item.coa || "";
        const acc = accountOptionsMap.get(String(c));
        const itType = getItemType(item);
        const isErTax = itType === ITEM_TYPE.EmployerTaxes;
        const expCoa = isErTax ? (item.LiabilityAccountID || item.ExpenseAccountID || item.ExpenseCOA || "") : "";
        const itemAmt = item.Amount ?? item.amount;
        return { ...existingRow, id: (!forceOverwrite && existingRow.id) ? existingRow.id : Date.now() + index, taxType: itType || existingRow.taxType, item: (!forceOverwrite && existingRow.item) ? existingRow.item : l, mappedTo: v, PayrollItemId: v, mappedToName: l, coa: c, coaName: item.AccountName || item.COAName || acc?.label || "", expenseCoa: expCoa, amount: (!forceOverwrite && existingRow.amount) ? existingRow.amount : (itemAmt != null ? Number(itemAmt).toFixed(2) : "0.00") };
    });
    if (!forceOverwrite && values.eeAndErTaxes && values.eeAndErTaxes.length > taxesData.length) {
        for (let i = taxesData.length; i < values.eeAndErTaxes.length; i++) {
            const row = values.eeAndErTaxes[i];
            if (row.item || (row.amount && Number(row.amount) !== 0)) mapped.push({ ...row, mappedTo: "", mappedToName: "", coa: "", coaName: "", expenseCoa: "" });
        }
    }
    return mapped;
}
